import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { outreachFetch, useBusiness } from "../lib/business";
import { research, type Sender } from "../lib/research";

// A template lives in Master and is made available to one or more companies.
// Sending happens inside a company (Contacts → Send email), with that company's sender.

interface Campaign {
  id: number; name: string; subject: string; body: string; status: string;
  from_name?: string | null; from_email?: string | null; reply_to?: string | null;
  created_by_email?: string | null; created_at: string;
  business_ids?: number[];
}
type CampaignDetail = { campaign: Campaign };
type SaveResp = { id?: number; error?: string };

export default function CampaignEdit() {
  const nav = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = id === "new";
  const { businesses } = useBusiness();
  const [c, setC] = useState<Partial<Campaign>>({
    name: "", subject: "", body: "<p>Hi {{first_name}},</p><p>…</p>",
    from_name: "", from_email: "", reply_to: "", business_ids: [],
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [senders, setSenders] = useState<Sender[]>([]);
  const [senderId, setSenderId] = useState<number | null>(null);

  useEffect(() => {
    research.senders().then(d => setSenders(d.senders.filter(s => !s.disabled))).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) return;
    outreachFetch(`/api/outreach/campaigns/${id}`)
      .then(r => r.json() as Promise<CampaignDetail>)
      .then(d => setC(d.campaign))
      .catch(e => setError(String(e)));
  }, [id, isNew]);

  const assigned = new Set(c.business_ids || []);
  function toggleCompany(bid: number) {
    const n = new Set(assigned);
    if (n.has(bid)) n.delete(bid); else n.add(bid);
    setC({ ...c, business_ids: [...n] });
  }

  async function save() {
    if (!assigned.size) { setError("Tick at least one company this template is for."); return; }
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await outreachFetch(isNew ? "/api/outreach/campaigns" : `/api/outreach/campaigns/${id}`, {
        method: isNew ? "POST" : "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(c),
      });
      const d = (await r.json()) as SaveResp;
      if (!r.ok) { setError(d.error || "Save failed"); return; }
      if (isNew && d.id) nav(`/outreach/campaigns/${d.id}`, { replace: true });
      else setNotice("Saved.");
    } finally { setBusy(false); }
  }

  async function testSend() {
    const to = prompt("Send a test of this template to which email?");
    if (!to) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await outreachFetch(`/api/outreach/campaigns/${id}/test-send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to, ...(senderId ? { sender_id: senderId } : {}) }),
      });
      const d = (await r.json()) as { ok?: boolean; id?: string; error?: string };
      if (!r.ok) { setError(d.error || "Test send failed"); return; }
      setNotice(`Test sent to ${to}.`);
    } finally { setBusy(false); }
  }

  const companyName = (bid: number) => businesses.find(b => b.id === bid)?.name || `#${bid}`;

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">{isNew ? "New template" : c.name || `Template #${id}`}</h1>
          <p className="mt-1 text-sm text-slate-500">
            Placeholders: <code>{"{{first_name}}"}</code>, <code>{"{{name}}"}</code>, <code>{"{{company}}"}</code>, <code>{"{{title}}"}</code>, <code>{"{{country}}"}</code>, <code>{"{{email}}"}</code>
          </p>
        </div>
        <Link to="/outreach/campaigns" className="btn-secondary">← Templates</Link>
      </div>

      {error && <div className="card p-4 text-sm text-rose-700">{error}</div>}
      {notice && <div className="card p-4 text-sm text-emerald-700">{notice}</div>}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="card space-y-3 p-5 lg:col-span-2">
          <Field label="Template name (internal)" value={c.name || ""} onChange={v => setC({ ...c, name: v })} />
          <Field label="Subject" value={c.subject || ""} onChange={v => setC({ ...c, subject: v })} />
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Body (HTML)</label>
            <textarea className="h-72 w-full font-mono text-xs" value={c.body || ""} onChange={e => setC({ ...c, body: e.target.value })} />
          </div>
        </div>

        <div className="space-y-4">
          <div className="card space-y-2 p-5">
            <div className="text-xs font-medium text-slate-600">Available to</div>
            {businesses.map(b => (
              <label key={b.id} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={assigned.has(b.id)} onChange={() => toggleCompany(b.id)} />
                {b.name}
              </label>
            ))}
            <p className="text-[11px] text-slate-500">Only these companies see this template when sending.</p>
          </div>

          <div className="card space-y-3 p-5">
            <Field label="From name (default)" value={c.from_name || ""} onChange={v => setC({ ...c, from_name: v })} />
            <Field label="From email (default)" value={c.from_email || ""} onChange={v => setC({ ...c, from_email: v })} placeholder="used only for e-Veritas without a sender" />
            <Field label="Reply-To (default)" value={c.reply_to || ""} onChange={v => setC({ ...c, reply_to: v })} />
            <p className="text-[11px] text-slate-500">When sending, the company's sender account decides the from-address.</p>
            <button disabled={busy} className="btn-primary w-full" onClick={save}>{busy ? "Saving…" : isNew ? "Save template" : "Save changes"}</button>
          </div>

          {!isNew && (
            <div className="card space-y-2 p-5">
              <label className="block text-xs font-medium text-slate-600">Send a test from</label>
              <select className="w-full !text-sm !py-2" value={senderId ?? ""} onChange={e => setSenderId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">Default e-Veritas account</option>
                {senders.map(s => <option key={s.id} value={s.id}>{companyName(s.business_id)} · {s.label} — {s.from_email}</option>)}
              </select>
              <button disabled={busy} className="btn-secondary w-full" onClick={testSend}>Send test to one email…</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
      <input
        type="text"
        className="w-full !text-sm !py-2 !px-3"
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
}
